/**
 * The shape of a Gmail authorisation as the TA board holds it, shared by the three ways of
 * obtaining one (server grant, GIS silent mint, Firebase popup — see useGmailToken.ts).
 */

/** Must match TA_INBOX_SCOPES in proxy-server/lib/taInboxAuth.js. */
export const GMAIL_SCOPES = [
  // gmail.modify (not readonly) since Aug 2026: Mark-as-read labels and archives the thread in the
  // recruiter's own Gmail, and no narrower scope can do either. See TA_INBOX_SCOPES for the full
  // reasoning and for what replaced "the scope cannot mutate anything" as the safety property.
  'https://www.googleapis.com/auth/gmail.modify',
  'https://www.googleapis.com/auth/gmail.compose',
  // Reads the role's kick-off sheet for the job description, as the signed-in recruiter — so the
  // board can only open documents they already have access to. See TA_INBOX_SCOPES.
  'https://www.googleapis.com/auth/drive.readonly',
];

/** Treat tokens as expired a minute early so a request never departs with one about to lapse. */
export const EXPIRY_SLACK_MS = 60_000;

export type GrantSource = 'server' | 'gis' | 'popup';

export interface Grant {
  token: string;
  owner: string;
  expiresAt: number;
  /**
   * The scopes that were REQUESTED when this token was minted.
   *
   * Without it, adding a scope broke the tab for up to an hour per recruiter: the cached token
   * predates the new scope, nothing in the grant said so, and it kept being sent until it expired.
   * Comparing against the current GMAIL_SCOPES makes a deploy that widens the list invalidate the
   * old grant immediately, so the recruiter is asked to reconnect instead of hitting failures.
   *
   * Requested, not granted — Firebase's credential does not report what Google actually approved.
   * The server-side check is the other half: a token genuinely missing a scope comes back as
   * NEEDS_RECONNECT rather than a silent 502.
   */
  scopes: string[];
  /** Where the token came from; decides whether it can be renewed silently. Absent on old entries. */
  source?: GrantSource;
}

/** True when a grant covers everything the app currently asks for. */
export function grantCoversScopes(grant: Grant): boolean {
  const held = new Set(grant.scopes || []);
  return GMAIL_SCOPES.every((s) => held.has(s));
}
